/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-08-27 15:00:00
 * @description Implementation of the Technical issues react component
 * @filename technical-issues.tsx
 */
import React from "react";

/**
 *
 * A React component that renders the Technical issues .
 *
 * @extends {React.Component}
 */
export class TechnicalIssues extends React.Component {
  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className="TechnicalIssues external inner-pages">
        <div className="title">
          <h1 className="font-weight-bold">Technical Issues</h1>
          <h4 className="font-weight-400">
            Common Device and Browser Issues You May Encounter
          </h4>
        </div>
        <p className="text-center font-weight-bold">
          Before you contact our team for help, please review the following
          solutions to common technical issues you may be experiencing.
        </p>
        <div className="question-and-answer text-left">
          <div>
            <h3 className="font-weight-bold">
              What do in the case of a device issue:
            </h3>
            <ol>
              <li>
                Try using another device:
                <span>
                  To figure out whether the issue is with your device (and not
                  with your browser or with our system), we recommend trying
                  another device first.
                </span>
              </li>
              <li>
                Restart your device:{" "}
                <span>
                  Restarting fixes small errors, installs updates, and stops
                  memory leaks. This is why we recommend restarting your device.
                  To restart, press and hold down the power button. After 5–10
                  seconds, the device should turn off. Once it is off, wait a
                  few seconds and then turn the device back on.
                </span>
              </li>
            </ol>
          </div>

          <div>
            <h3 className="font-weight-bold">
              What to do in the case of a browser issue:
            </h3>
            <ol>
              <li>
                Update your browser:{" "}
                <span>
                  The site supports all popular browsers including Internet
                  Explorer V11 or later. However, updating your browser is
                  recommended if it hasn’t been updated in the last year.
                </span>
              </li>
              <li>
                Restart your browser:{" "}
                <span>
                  Sometimes browsers can store old versions of pages to help
                  them load faster (called “caching”). You might need to refresh
                  your browser. To do this, click the refresh icon (⟳) on your
                  browser’s toolbar at the top of the page. If this doesn’t
                  work, quit the browser completely and reopen.
                </span>
              </li>
              <li>
                Clear your cache, cookies, and browsing history:
                <span>
                  {" "}
                  For browser specific instructions on how to do this, go here:
                  <br />
                  <a
                    href="https://support.google.com/accounts/answer/32050?co=GENIE.Platform%3DDesktop&hl=en"
                    target="_blank"
                  >
                    Google Chrome Help Page
                  </a>
                  <br />
                  <a
                    href="https://support.mozilla.org/en-US/kb/how-clear-firefox-cache"
                    target="_blank"
                  >
                    Mozilla Firefox Help Page
                  </a>
                  <br />
                  <a
                    href="https://support.microsoft.com/en-us/help/278835/how-to-delete-cookie-files-in-internet-explorer"
                    target="_blank"
                  >
                    Internet Explorer Help Page
                  </a>
                </span>
              </li>
            </ol>
          </div>
        </div>

        <h2 className="font-weight-bold text-left">Need more help?</h2>
        <p>
          If you have tried all of the suggested solutions above and are still
          experiencing issues, please email us at{" "}
          <a href="mailto:support@radivision.com">support@radivision.com.</a>
        </p>
      </div>
    );
  }
}
